/**
 * What the outstanding-questions inbox says, decided before anything is drawn.
 *
 * A question raised to the owner is owed an answer whether or not anybody is
 * still looking at its alert. Until this surface existed a question was
 * reachable only through its notification or a direct link, so dismissing the
 * doorbell hid work that was still pending. The inbox is the list that cannot
 * be dismissed.
 *
 * Two rules live here and nowhere else:
 *
 * 1. **The counts are the server's, not the page's.** The listing returns
 *    tenant-wide outstanding counts independent of whatever this page filtered
 *    to, so the summary reports what is owed rather than what happens to be on
 *    screen. A page showing five rows out of forty says forty.
 * 2. **A partial list says it is partial.** A keyset listing that stops at a
 *    cursor and renders like a complete one is the same quiet dishonesty the
 *    thread history refuses. Whenever more rows exist, this says so.
 *
 * Mirrored in `frontends/apple/recallvault/QuestionInbox.swift` sentence for
 * sentence, under the same test names, so the two clients cannot drift.
 */

import type { LedgerRequest, LedgerRequestState } from "./platform-client.js";

/** How much is outstanding tenant-wide, as the listing endpoint reports it. */
export interface OutstandingCounts {
  readonly pending: number;
  /** Answered and nothing has consumed them yet. */
  readonly answeredNotConsumed: number;
}

/**
 * What the inbox defaults to: questions asked of the owner that nobody has
 * answered. That is the question an inbox answers — "what do I owe?" — and it
 * is the same pair of filters the summary counts describe.
 */
export const INBOX_DIRECTION = "to_owner" as const;
export const INBOX_STATES: readonly LedgerRequestState[] = ["pending"];

/** The one line above the list: what is owed, in the server's numbers. */
export function outstandingSummary(counts: OutstandingCounts): string {
  const waiting = counts.pending === 0
    ? "Nothing is waiting on you."
    : counts.pending === 1
      ? "1 question is waiting on you."
      : `${counts.pending} questions are waiting on you.`;
  if (counts.answeredNotConsumed === 0) return waiting;
  const carried = counts.answeredNotConsumed === 1
    ? "1 answer is waiting to be picked up."
    : `${counts.answeredNotConsumed} answers are waiting to be picked up.`;
  return `${waiting} ${carried}`;
}

/** What an inbox with no rows on it says instead of a list. */
export const NOTHING_OUTSTANDING = "No question is waiting for your answer.";

/**
 * What a list that stopped at a cursor says.
 *
 * Empty when the list is whole, so a caller can render it unconditionally and
 * still never claim a complete list is partial.
 */
export function moreToRead(hasMore: boolean): string {
  return hasMore ? "More questions matched than this page holds." : "";
}

/**
 * What a thread history that hit the client's page cap says.
 *
 * The one sentence that keeps a truncated thread honest. It is here rather than
 * beside the thread renderer so both clients say it identically.
 */
export const HISTORY_TRUNCATED =
  "This thread is longer than this client read. Earlier messages are not shown.";
